/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { invalidateAuditLogQueries } from "../../utils/invalidateAuditLogQueries";
import React, { useState } from "react";
import { NavigableBreadcrumbLink } from "../../components/page_breadcrumbs/navigable_breadcrumb_link";
import { RestaurantTable } from "../../components/restaurants_table/index.jsx";
import { DepartmentsChip } from "../../components/departments_chip/index.jsx";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Divider,
  Breadcrumbs,
  FormControl,
  Alert,
  Select,
  Tooltip,
  Switch,
  Chip,
  InputAdornment
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { tooltipClasses } from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { useOtMutation } from "../../hooks/mutations/useOtMutation.js";
import { restaurantMutationKeyGroups, restaurantRemoveKeyGroups } from "../../hooks/mutations/queryKeyGroups/restaurantMutationKeys.js";
import { employeeMutationKeyGroups } from "../../hooks/mutations/queryKeyGroups/employeeMutationKeys.js";
import { EmployeeCreateModal } from "../../components/employee_create_modal";
import { NetworkGuestCreateModal } from "../../components/create_guest_modal";
import ManualCsvUploadEmployee from "../../components/manual_csv_employee_import/index.jsx";
import { AddMobileAppUsersMenu } from "../../components/add_mobile_app_users_menu";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from "@mui/icons-material/Delete";
import TranslateIcon from '@mui/icons-material/Translate';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import {
  RestaurantPageContainer,
  StyledBreadCrumbContainer,
  StyledLink,
  StyledTitleContainer,
  StyledTitle,
  StyledButton,
  StyledInputLabel,
} from "./styles.jsx";
import api from "../../api/index.js";
import authenticatedFetch from "../../api/authenticatedFetch";
import { useNotificationContext } from "../../contexts/notificationContext.jsx";
import { AbilityContext } from '../../contexts/abilityContext.jsx'
import { FilterContainer } from "../../components/filter_container/index.jsx";
import { selectBaseStyles, viewTypographyStyles } from "../reports/styles.js";
import { UpdateButton } from "../../components/update_button/index.jsx";
import { strictCanScope } from '../../utils/caslStrict.js'
import { DropdownInfiniteLoader } from "../../components/dropdown_infinite_loader";
import { useInfiniteQuery, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { Loader } from "../../components/loader/index.jsx";
import { ConfirmationDialog } from "../../components/confirmation_dialog/index.jsx";
import { LoadingSpinner } from "../../components/loading_spinner/index.jsx";
import DropdownSearchBar from "../../components/dorpdown_searchbar/index.jsx";
import { formatDate } from '../../utils/formatDate.js'
import { SelectedMenuItem } from '../../components/selected_menu_item/index.jsx'
import { TranslationModal } from '../../components/translation_modal/index.jsx'
import { buildQueryParams } from "../../utils/buildQueryParams.js";
import dayjs from "dayjs";
import { StyledMenuItem } from "../../components/styled_menu_item/index.jsx";
import { StyledProgress } from "./create_restaurant/styles.jsx";
import { Query_Keys } from "../../lib/queryKeys.js";
import { StyledSearchBar } from "../reports/global_employee_search/styles.jsx";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { ResetButton } from "../../components/reset_button";
import { useDebounce } from "../../utils/useDebounce.js";
import { PerPage } from "../../constants/perPage.js";
import { getDropdownNextPageParam, mergeOptionsIfChanged, mergeUniqueByKey, normalizeRestaurant, normalizeRestaurantGroup } from "../../utils/dropdownInfiniteQuery.js";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { resetPageAfterDeletion } from '../../utils/resetPageAfterDeletion'
import { fonts } from "../../themes/dashboard";

const RestaurantLightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[1],
    fontSize: '14px',
    padding: '12px',
    maxWidth: '214px',
    fontFamily: fonts?.fontFamily,
    fontWeight: '400',
    letterSpacing: '0px',
  },
}));

const TOAST_IMPORT_STATUS = Object.freeze({
  ACTIVE: "active",
  INACTIVE: "inactive",
  UN_INVOKABLE: "un_invokable",
});

const EMPTY_FILTERS = {
  restaurant_group_id: '',
  restaurant_sub_group_id: '',
  restaurant_id: [],
}

const EMPTY_DROPDOWN_OPTIONS = []

const hasActiveRestaurantFilters = (filters) => {
  const hasRestaurantGroup = Boolean(filters?.restaurant_group_id);
  const hasRestaurantSubGroup = Boolean(filters?.restaurant_sub_group_id);
  const hasRestaurantId = Array.isArray(filters?.restaurant_id)
    ? filters.restaurant_id.length > 0
    : Boolean(filters?.restaurant_id);

  return hasRestaurantGroup || hasRestaurantSubGroup || hasRestaurantId;
};

const VALID_TOAST_IMPORT_STATUSES = new Set(Object.values(TOAST_IMPORT_STATUS));

const EMPLOYEE_CSV_IMPORT_ERROR_KEYS = ["file_type", "employees_data", "headers", "header_mapping"];

const getEmployeeCsvImportErrorMessage = (error) => {
  const errors = error?.response?.data?.errors;

  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return "Failed to create employees";
  }

  const [firstErrorEntry] = Object.entries(errors);

  if (!firstErrorEntry) {
    return "Failed to create employees";
  }

  const [key, value] = firstErrorEntry;

  if (!EMPLOYEE_CSV_IMPORT_ERROR_KEYS.includes(key)) {
    return "Failed to create employees";
  }

  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value) && value.length > 0) {
    return value[0];
  }

  return "Failed to create employees";
};

const getToastImportStatus = (record) => {
  if (VALID_TOAST_IMPORT_STATUSES.has(record?.toast_import_status)) {
    return record.toast_import_status;
  }

  return TOAST_IMPORT_STATUS.INACTIVE;
};

const getToastImportMeta = (record, theme) => {
  const toastImportStatus = getToastImportStatus(record);

  switch (toastImportStatus) {
    case TOAST_IMPORT_STATUS.ACTIVE:
      return {
        label: "Revoke",
        tooltip: "Revoke the sync jobs",
        backgroundColor: theme.palette.grey[700],
        hoverBackgroundColor: theme.palette.grey[800],
        textColor: theme.palette.getContrastText(theme.palette.grey[700]),
        cursor: "pointer",
        disabled: false,
      };
    case TOAST_IMPORT_STATUS.UN_INVOKABLE: {
      const uninvokableBackgroundColor = theme.palette.warning.dark || theme.palette.warning.main;
      return {
        label: "Add Credentials",
        tooltip: "Add or update credentials to enable job execution.",
        backgroundColor: uninvokableBackgroundColor,
        hoverBackgroundColor: uninvokableBackgroundColor,
        textColor: theme.palette.getContrastText(uninvokableBackgroundColor),
        cursor: "not-allowed",
        disabled: true,
      };
    }
    case TOAST_IMPORT_STATUS.INACTIVE:
    default:
      return {
        label: "Invoke",
        tooltip: "Invoke data sync",
        backgroundColor: theme.palette.primary.main,
        hoverBackgroundColor: theme.palette.primary.dark || theme.palette.primary.main,
        textColor: theme.palette.getContrastText(theme.palette.primary.main),
        cursor: "pointer",
        disabled: false,
      };
  }
};

export default function Restaurant() {
  const singleRestaurantGroupAutoApplied = React.useRef(false)
  const singleRestaurantAutoApplied = React.useRef(false)
  const [itemsPerPage, setItemsPerPage] = React.useState(25);
  const [page, setPage] = React.useState(1);
  const [orderBy, setOrderBy] = React.useState("created_at");
  const [order, setOrder] = React.useState("desc");
  const location = useLocation();
  const initialFilters = {
    restaurant_id: location?.state?.restaurantId || [],
    restaurant_group_id: location?.state?.restaurantGroupId || '',
    restaurant_sub_group_id: location?.state?.restaurantSubGroupId || '',
  }
  const initialSelectedRestaurantGroup = initialFilters.restaurant_group_id || '';
  const initialSelectedRestaurantSubGroup = initialFilters.restaurant_sub_group_id || '';
  const initialSelectedRestaurantFilterId = Array.isArray(initialFilters.restaurant_id)
    ? (initialFilters.restaurant_id[0] || '')
    : (initialFilters.restaurant_id || '');
  const [filters, setFilters] = useState(initialFilters);
  const [filtersChanged, setFiltersChanged] = useState(false);
  const [restaurantGroupKey, setRestaurantGroupKey] = React.useState('')
  const [createPermission, setCreatePermission] = React.useState(false)
  const [deleteInstancePermission, setDeleteInstancePermission] = useState(false);
  const queryClient = useQueryClient();
  const [filtersAutoApplyReady, setFiltersAutoApplyReady] = React.useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState({})
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingStatus, setLoadingStatus] = React.useState(false);
  const [visibilityDialogOpen, setVisibilityDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [restaurantId, setRestaurantId] = useState('')
  const [restaurantGroupSearch, setRestaurantGroupSearch] = useState("");
  const [restaurantSubGroupSearch, setRestaurantSubGroupSearch] = useState("");
  const [restaurantSearch, setRestaurantSearch] = useState("")
  const [translationModalOpen, setTranslationModalOpen] = useState(false)
  const [selectedRestaurantForTranslation, setSelectedRestaurantForTranslation] = useState(null)
  const [isTranslationSaving, setIsTranslationSaving] = useState(false)
  const [translationLoading, setTranslationLoading] = useState(false)
  const navigate = useNavigate();
  const [appVisibilityPermission, setAppVisibilityPermission] = useState(false)
  const [csvLoading, setCsvLoading] = useState(false)
  const [appliedFilters, setAppliedFilters] = useState(initialFilters)
  const [guestCreateAnchorEl, setGuestCreateAnchorEl] = useState(null)
  const [guestCreateRecord, setGuestCreateRecord] = useState(null)
  const [activeGuestRestaurant, setActiveGuestRestaurant] = useState(null)
  const [employeeCreateModalOpen, setEmployeeCreateModalOpen] = useState(false)
  const [networkGuestCreateModalOpen, setNetworkGuestCreateModalOpen] = useState(false)
  const [csvImportModalOpen, setCsvImportModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [restaurantOptionsCache, setRestaurantOptionsCache] = useState([])
  const [restaurantGroupOptionsCache, setRestaurantGroupOptionsCache] = useState([])
  const [restaurantSubGroupOptionsCache, setRestaurantSubGroupOptionsCache] = useState([])
  const [prefillRestaurantGroupName, setPrefillRestaurantGroupName] = useState('')
  const [prefillRestaurantSubGroupName, setPrefillRestaurantSubGroupName] = useState('')
  const [prefillRestaurantName, setPrefillRestaurantName] = useState('')
  const debouncedSearchTerm = useDebounce(search, 300);
  const debouncedRestaurantGroupSearch = useDebounce(restaurantGroupSearch.trim(), 300);
  const debouncedRestaurantSubGroupSearch = useDebounce(restaurantSubGroupSearch.trim(), 300);
  const debouncedRestaurantSearch = useDebounce(restaurantSearch.trim(), 300);
  const theme = useTheme();

  const showNotification = useNotificationContext();
  const [selectedRestaurantGroup, setSelectedRestaurantGroup] =
    React.useState(initialSelectedRestaurantGroup);
  const [selectedRestaurantSubGroup, setSelectedRestaurantSubGroup] =
    React.useState(initialSelectedRestaurantSubGroup);
  const { ability } = React.useContext(AbilityContext)
  const [selectedRestaurantFilterId, setSelectedRestaurantFilterId] = useState(initialSelectedRestaurantFilterId)
  const defaultCreatePermission = strictCanScope(ability, 'create_restaurant', 'Restaurant_Group')

  React.useEffect(() => {
    const previousFilters = location.state?.prefillFilters

    if (!previousFilters?.restaurantGroupId && !previousFilters?.restaurantId) {
      return
    }

    const restoredRestaurantGroupId = previousFilters?.restaurantGroupId || ''
    const restoredRestaurantSubGroupId = previousFilters?.restaurantSubGroupId || ''
    const restoredRestaurantId = previousFilters?.restaurantId ? Number(previousFilters.restaurantId) : ''
    const restoredRestaurantGroupName = previousFilters?.restaurantGroupName || ''
    const restoredRestaurantSubGroupName = previousFilters?.restaurantSubGroupName || ''
    const restoredRestaurantName = previousFilters?.restaurantName || ''

    if (restoredRestaurantGroupId) {
      setSelectedRestaurantGroup(restoredRestaurantGroupId)
      setRestaurantGroupKey(`${restoredRestaurantGroupId}`)
    }

    if (restoredRestaurantSubGroupId) {
      setSelectedRestaurantSubGroup(restoredRestaurantSubGroupId)
    }

    if (restoredRestaurantId) {
      setSelectedRestaurantFilterId(restoredRestaurantId)
    }

    setPrefillRestaurantGroupName(restoredRestaurantGroupName)
    setPrefillRestaurantSubGroupName(restoredRestaurantSubGroupName)
    setPrefillRestaurantName(restoredRestaurantName)

    if (restoredRestaurantGroupName) {
      setRestaurantGroupSearch(restoredRestaurantGroupName)
    }

    if (restoredRestaurantSubGroupName) {
      setRestaurantSubGroupSearch(restoredRestaurantSubGroupName)
    }

    if (restoredRestaurantName) {
      setRestaurantSearch(restoredRestaurantName)
    }

    setFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: restoredRestaurantGroupId,
      restaurant_sub_group_id: restoredRestaurantSubGroupId,
      restaurant_id: restoredRestaurantId,
    }))

    setAppliedFilters((currentFilters) => ({
      ...currentFilters,
      restaurant_group_id: restoredRestaurantGroupId,
      restaurant_sub_group_id: restoredRestaurantSubGroupId,
      restaurant_id: restoredRestaurantId,
    }))

    setFiltersAutoApplyReady(true)
  }, [location.state])

  const fetchRestaurantGroups = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurant_groups_list', {
      params: {
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantGroupSearch || undefined,
      },
    })
    return response?.data?.map(normalizeRestaurantGroup) || []
  }

  const {
    data: restaurantGroupList = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantGroupFetching,
    isFetchingNextPage: restaurantGroupListFetchingNextPage,
    fetchNextPage: fetchNextRestaurantGroupListPage,
    hasNextPage: hasNextRestaurantGroupListPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_groups_dropdown({ search: debouncedRestaurantGroupSearch }),
    queryFn: fetchRestaurantGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurantSubGroups = async ({ pageParam = 1 }) => {
    if (!selectedRestaurantGroup) return []

    const response = await api.get('/v1/admin/dropdowns/restaurant_sub_groups_list', {
      params: {
        restaurant_group_id: selectedRestaurantGroup,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSubGroupSearch || undefined,
      },
    })

    return response?.data || []
  }

  const {
    data: restaurantSubGroupList = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantSubGroupFetching,
    isFetchingNextPage: restaurantSubGroupListFetchingNextPage,
    fetchNextPage: fetchNextRestaurantSubGroupListPage,
    hasNextPage: hasNextRestaurantSubGroupListPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurant_sub_groups_dropdown({
      restaurant_group_id: selectedRestaurantGroup,
      search: debouncedRestaurantSubGroupSearch,
    }),
    queryFn: fetchRestaurantSubGroups,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    enabled: Boolean(selectedRestaurantGroup),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const fetchRestaurants = async ({ pageParam = 1 }) => {
    const response = await api.get('/v1/admin/dropdowns/restaurants_list', {
      params: {
        restaurant_group_ids: selectedRestaurantGroup || undefined,
        restaurant_sub_group_id: selectedRestaurantSubGroup || undefined,
        page: pageParam,
        per_page: PerPage,
        search: debouncedRestaurantSearch || undefined,
      },
    })

    return response?.data?.map(normalizeRestaurant) || []
  }

  const {
    data: restaurants = EMPTY_DROPDOWN_OPTIONS,
    isFetching: restaurantFetching,
    isFetchingNextPage: restaurantsFetchingNextPage,
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
  } = useInfiniteQuery({
    queryKey: Query_Keys.restaurants_dropdown({
      restaurant_group_id: selectedRestaurantGroup,
      restaurant_sub_group_id: selectedRestaurantSubGroup,
      search: debouncedRestaurantSearch,
    }),
    queryFn: fetchRestaurants,
    initialPageParam: 1,
    getNextPageParam: getDropdownNextPageParam,
    select: (data) => data.pages.flat(),
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  })

  const { rootRef: restaurantGroupMenuRef, targetRef: restaurantGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantGroupListPage,
    hasNextPage: hasNextRestaurantGroupListPage,
    isFetchingNextPage: restaurantGroupListFetchingNextPage,
  })

  const { rootRef: restaurantSubGroupMenuRef, targetRef: restaurantSubGroupLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantSubGroupListPage,
    hasNextPage: hasNextRestaurantSubGroupListPage,
    isFetchingNextPage: restaurantSubGroupListFetchingNextPage,
  })

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  })

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const dialogBody = () => {
    return (
      <>
        {'This action will permanently delete the'}
        <span style={{ fontWeight: "600" }}>
          {` '${selectedRestaurant?.name}'`}
        </span>
      </>
    );
  };

  React.useEffect(() => {
    setRestaurantGroupOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurantGroupList, 'label'))
  }, [restaurantGroupList])

  React.useEffect(() => {
    setRestaurantSubGroupOptionsCache((currentOptions) => mergeOptionsIfChanged(currentOptions, restaurantSubGroupList, 'value', ['label']))
  }, [restaurantSubGroupList])

  React.useEffect(() => {
    setRestaurantOptionsCache((currentOptions) => mergeUniqueByKey(currentOptions, restaurants, 'id'))
  }, [restaurants])

  React.useEffect(() => {
    if (!prefillRestaurantGroupName) {
      return
    }

    const matchingRestaurantGroup = restaurantGroupOptionsCache?.find((restaurantGroup) => (
      String(restaurantGroup?.label) === String(location.state?.prefillFilters?.restaurantGroupId)
        || restaurantGroup?.value === prefillRestaurantGroupName
    ))

    if (!matchingRestaurantGroup) {
      return
    }

    setSelectedRestaurantGroup(matchingRestaurantGroup?.label)
    setRestaurantGroupKey(`${matchingRestaurantGroup?.label}`)

    if (restaurantGroupSearch === prefillRestaurantGroupName) {
      setRestaurantGroupSearch('')
    }

    setPrefillRestaurantGroupName('')
  }, [location.state?.prefillFilters?.restaurantGroupId, prefillRestaurantGroupName, restaurantGroupOptionsCache, restaurantGroupSearch])

  React.useEffect(() => {
    if (!prefillRestaurantSubGroupName) {
      return
    }

    const matchingRestaurantSubGroup = restaurantSubGroupOptionsCache?.find((subGroup) => (
      String(subGroup?.value) === String(location.state?.prefillFilters?.restaurantSubGroupId)
        || subGroup?.label === prefillRestaurantSubGroupName
    ))

    if (!matchingRestaurantSubGroup) {
      return
    }

    setSelectedRestaurantSubGroup(matchingRestaurantSubGroup?.value)

    if (restaurantSubGroupSearch === prefillRestaurantSubGroupName) {
      setRestaurantSubGroupSearch('')
    }

    setPrefillRestaurantSubGroupName('')
  }, [location.state?.prefillFilters?.restaurantSubGroupId, prefillRestaurantSubGroupName, restaurantSubGroupOptionsCache, restaurantSubGroupSearch])

  React.useEffect(() => {
    if (!prefillRestaurantName) {
      return
    }

    const matchingRestaurant = restaurantOptionsCache?.find((restaurant) => (
      String(restaurant?.id) === String(location.state?.prefillFilters?.restaurantId)
        || restaurant?.name === prefillRestaurantName
    ))

    if (!matchingRestaurant) {
      return
    }

    setSelectedRestaurantFilterId(matchingRestaurant?.id)

    if (restaurantSearch === prefillRestaurantName) {
      setRestaurantSearch('')
    }

    setPrefillRestaurantName('')
  }, [location.state?.prefillFilters?.restaurantId, prefillRestaurantName, restaurantOptionsCache, restaurantSearch])

  const hasSingleAvailableRestaurantGroup = !debouncedRestaurantGroupSearch && restaurantGroupList.length === 1
  const hasSingleAvailableRestaurant = !debouncedRestaurantSearch && restaurants.length === 1

  React.useEffect(() => {
    if (filters && restaurantGroupKey) {
      setCreatePermission(strictCanScope(ability, 'create_restaurant', 'Restaurant_Group', `${restaurantGroupKey}`))
      setDeleteInstancePermission(strictCanScope(ability, 'delete_restaurant', 'Restaurant_Group', `${restaurantGroupKey}`));
      setAppVisibilityPermission(strictCanScope(ability, 'create_restaurant', 'Restaurant_Group', `${restaurantGroupKey}`))
    }
  }, [filters, restaurantGroupList])

  React.useEffect(() => {
    if (selectedRestaurantGroup || prefillRestaurantGroupName || singleRestaurantGroupAutoApplied.current || !hasSingleAvailableRestaurantGroup) {
      return
    }

    const id = restaurantGroupList?.[0]?.label

    singleRestaurantGroupAutoApplied.current = true
    setSelectedRestaurantGroup(id);
    setFilters((currentFilters) => ({ ...currentFilters, restaurant_group_id: id }));
    setAppliedFilters((currentFilters) => ({ ...currentFilters, restaurant_group_id: id }));
    setRestaurantGroupKey(`${id}`)
    setFiltersAutoApplyReady(true)
  }, [hasSingleAvailableRestaurantGroup, prefillRestaurantGroupName, restaurantGroupList, selectedRestaurantGroup])

  React.useEffect(() => {
    if (selectedRestaurantFilterId || prefillRestaurantName || singleRestaurantAutoApplied.current || !restaurants || !hasSingleAvailableRestaurant) {
      return
    }

    const onlyRestaurantId = restaurants[0]?.id
    if (!onlyRestaurantId) {
      return
    }

    singleRestaurantAutoApplied.current = true
    setSelectedRestaurantFilterId((currentRestaurantId) => currentRestaurantId || onlyRestaurantId)

    if (filtersAutoApplyReady) {
      setFilters((currentFilters) => ({
        ...currentFilters,
        restaurant_group_id: currentFilters.restaurant_group_id || selectedRestaurantGroup,
        restaurant_id: onlyRestaurantId,
      }))

      setAppliedFilters((currentFilters) => ({
        ...currentFilters,
        restaurant_group_id: currentFilters.restaurant_group_id || selectedRestaurantGroup,
        restaurant_id: onlyRestaurantId,
      }))
    }
  }, [hasSingleAvailableRestaurant, prefillRestaurantName, restaurants, selectedRestaurantFilterId, selectedRestaurantGroup, filtersAutoApplyReady])

  const fetchRestaurantsList = async () => {
    const response = await api.get("/v1/admin/restaurants", {
      params: {
        per_page: itemsPerPage,
        page: page,
        sort_by: orderBy,
        sort_order: order,
        restaurant_group_ids: appliedFilters?.restaurant_group_id && [
          appliedFilters.restaurant_group_id,
        ],
        restaurant_sub_group_id: appliedFilters?.restaurant_sub_group_id || undefined,
        restaurant_ids: appliedFilters?.restaurant_id && [
          appliedFilters.restaurant_id,
        ],
        search_query: debouncedSearchTerm
      },
    })
    return { data: response?.data, pageCount: response?.headers["total-count"] }
  }

  const { data: restaurantData, isFetching } = useQuery({
    queryKey: Query_Keys.restaurants({
      queryFilter: appliedFilters,
      page: page,
      itemsPerPage: itemsPerPage,
      search_query: debouncedSearchTerm,
      orderBy,
      order,
    }),
    queryFn: fetchRestaurantsList,
    staleTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled: hasActiveRestaurantFilters(appliedFilters)
  })

  const deleteRestaurantMutation = useOtMutation({
    mutationFn: (id) => api.delete(`/v1/admin/restaurants/${id}`),
    invalidateKeys: restaurantMutationKeyGroups.delete,
    successMessage: "Restaurant deleted successfully",
    onSettled: () => {
      setDialogOpen(false);
      setLoadingStatus(false);
      setSelectedRestaurant(null);
    },
    errorMessage: (error) => error?.response?.data?.error
  });

  const changeVisibilityMutation = useOtMutation({
    mutationFn: ({ id, is_visible }) => api.put(`/v1/admin/restaurants/${id}/change_visibility`, { restaurant: { is_visible } }),
    invalidateKeys: restaurantMutationKeyGroups.toggleVisibility,
    successMessage: "Restaurant visibility changed successfully",
    onSettled: () => {
      setVisibilityDialogOpen(false);
      setLoadingStatus(false);
    },
    errorMessage: (error) => error?.response?.data?.error
  });

  const invokeJobsMutation = useOtMutation({
    mutationFn: (id) => api.put(`/v1/admin/restaurants/${id}/invoke_sidekiq_jobs`),
    removeKeys: restaurantRemoveKeyGroups.invokeJobs,
    successMessage: "Jobs invoked successfully",
    onSettled: () => {
      setRestaurantId(null);
    },
    errorMessage: (error) => error?.response?.data?.errors?.base
  });

  const revokeJobsMutation = useOtMutation({
    mutationFn: (id) => api.put(`/v1/admin/restaurants/${id}/revoke_sidekiq_jobs`),
    removeKeys: restaurantRemoveKeyGroups.revokeJobs,
    successMessage: "Jobs revoked successfully",
    onSettled: () => {
      setRevokeDialogOpen(false);
      setLoadingStatus(false);
      setRestaurantId(null);
    },
    errorMessage: (error) => error?.response?.data?.error?.error?.[0]
  });

  const updateTranslationsMutation = useOtMutation({
    mutationFn: ({ id, translations }) => api.put(`/v1/admin/restaurants/${id}/update_translations`, { translations }),
    invalidateKeys: restaurantMutationKeyGroups.updateTranslations,
    successMessage: "Translations updated successfully",
    onSettled: () => {
      setIsTranslationSaving(false);
      setTranslationModalOpen(false);
      setSelectedRestaurantForTranslation(null);
    },
    errorMessage: (error) => error?.response?.data?.error || 'Error saving translation'
  });

  const importEmployeesCsvMutation = useOtMutation({
    mutationFn: ({ file, restaurantId, headerMapping }) => {
      const formData = new FormData();
      formData.append('employees_data', file);
      formData.append('restaurant_id', restaurantId);
      Object.entries(headerMapping || {}).forEach(([csvHeader, mappedField]) => {
        formData.append(`header_mapping[${csvHeader}]`, mappedField);
      });

      return api.post('/v1/admin/employees/import_employees_data', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    invalidateKeys: employeeMutationKeyGroups.create,
    errorMessage: ({ error }) => getEmployeeCsvImportErrorMessage(error),
    onSettled: () => {
      invalidateAuditLogQueries(queryClient);
    },
  });

  const handleOpenGuestCreateMenu = (event, record) => {
    setGuestCreateAnchorEl(event.currentTarget);
    setGuestCreateRecord(record);
  };

  const handleCloseGuestCreateMenu = () => {
    setGuestCreateAnchorEl(null);
  };

  const handleOpenEmployeeCreateModal = () => {
    setActiveGuestRestaurant(guestCreateRecord);
    setEmployeeCreateModalOpen(true);
    handleCloseGuestCreateMenu();
  };

  const handleOpenNetworkGuestCreateModal = () => {
    setActiveGuestRestaurant(guestCreateRecord);
    setNetworkGuestCreateModalOpen(true);
    handleCloseGuestCreateMenu();
  };

  const handleOpenCsvImportModal = () => {
    setActiveGuestRestaurant(guestCreateRecord);
    setCsvImportModalOpen(true);
    handleCloseGuestCreateMenu();
  };

  const handleCloseCsvImportModal = () => {
    if (importEmployeesCsvMutation.isPending) {
      return;
    }

    setCsvImportModalOpen(false);
  };

  const handleUploadEmployeesCsv = async ({ file, restaurantId, headerMapping }) => {
    const response = await importEmployeesCsvMutation.mutateAsync({ file, restaurantId, headerMapping });
    if (response?.status !== 200) {
      throw new Error('Failed to create employees');
    }

    return response?.data || null;
  };

  const handleDelete = async () => {
    setLoadingStatus(true);
    deleteRestaurantMutation.mutate(selectedRestaurant.id, {
      onSuccess: () => {
          resetPageAfterDeletion({ totalCount: restaurantData?.pageCount, removed: 1, page, itemsPerPage, setPage })
      }
    });
  }

  const handleVisibility = async () => {
    setLoadingStatus(true);
    changeVisibilityMutation.mutate({
      id: selectedRestaurant.id,
      is_visible: !selectedRestaurant?.app_visibility
    });
  }

  const invokeBackgroundJobs = async (restaurantId) => {
    setRestaurantId(restaurantId)
    invokeJobsMutation.mutate(restaurantId);
  }

  const handleToastImportClick = (record, toastImportStatus, toastImportMeta) => {
    if (toastImportStatus === TOAST_IMPORT_STATUS.ACTIVE) {
      setRevokeDialogOpen(true)
      setSelectedRestaurant(record)
      return
    }

    if (toastImportMeta.disabled) {
      return
    }

    invokeBackgroundJobs(record?.id)
  }

  const revokeBackgroundJobs = async () => {
    setLoadingStatus(true)
    revokeJobsMutation.mutate(selectedRestaurant.id);
  }

  const handleOpenTranslationModal = (restaurant) => {
    setSelectedRestaurantForTranslation(restaurant)
    setTranslationModalOpen(true)
  }

  const handleCloseTranslationModal = () => {
    setTranslationModalOpen(false)
    setSelectedRestaurantForTranslation(null)
  }

  const handleSaveTranslation = async (translationData) => {
    setIsTranslationSaving(true)
    updateTranslationsMutation.mutate({
      id: translationData.id,
      translations: {
        spanish_description: translationData.spanish_description,
        spanish_address: translationData.spanish_address,
        spanish_cuisine_name: translationData.spanish_cuisine_name,
        spanish_accessibility: translationData.spanish_accessibility,
        spanish_dress_code: translationData.spanish_dress_code,
        spanish_parking: translationData.spanish_parking,
        spanish_payment_options: translationData.spanish_payment_options,
        spanish_amount_per_guest: translationData.spanish_amount_per_guest,
      },
    });
  }

  const getRestaurantGroupName = (id) => {
    const restaurantGroup = restaurantGroupOptionsCache?.find((res) => res.label === Number(id) || res.label === id)
    return restaurantGroup ? restaurantGroup?.value : prefillRestaurantGroupName
  }

  const getRestaurantSubGroupName = (id) => {
    if (!id) return ''

    const restaurantSubGroup = restaurantSubGroupOptionsCache?.find((res) => res.value === Number(id) || res.value === id)
    return restaurantSubGroup ? restaurantSubGroup?.label : prefillRestaurantSubGroupName
  }

  const getRestaurantName = (id) => {
    const restaurant = restaurantOptionsCache?.find((res) => res.id === Number(id) || res.id === id)
    return restaurant ? restaurant?.name : prefillRestaurantName
  }


  const handleOpen = () => {
    navigate('/admin/restaurant/create', {state: {
      restaurantGroupID: selectedRestaurantGroup,
      restaurantGroupName: getRestaurantGroupName(selectedRestaurantGroup),
      defaultCreatePermission: defaultCreatePermission,
      instanceCreatePermission: createPermission,
    }})
  };

  const handleSelectRestaurantGroup = (event) => {
    setSelectedRestaurantGroup(event.target.value);
    setSelectedRestaurantSubGroup('')
    setSelectedRestaurantFilterId('')
    setRestaurantSubGroupSearch('')
    setPrefillRestaurantGroupName('')
    setPrefillRestaurantSubGroupName('')
    setPrefillRestaurantName('')

    const restaurantGroup = restaurantGroupList?.find((data) => {
      if (data?.label === event.target.value) {
        return data
      }
    })
    setRestaurantGroupKey(restaurantGroup?.label)
    setCreatePermission(strictCanScope(ability, 'create_restaurant', 'Restaurant_Group', `${restaurantGroup?.label}`))

    setFiltersChanged(true);
    setFiltersAutoApplyReady(false)
  };

  const handleSelectRestaurantSubGroup = (event) => {
    setSelectedRestaurantSubGroup(event.target.value)
    setSelectedRestaurantFilterId('')
    setPrefillRestaurantSubGroupName('')
    setPrefillRestaurantName('')
    setFiltersChanged(true)
  }

  const handleRestaurantSubGroupClear = () => {
    setSelectedRestaurantSubGroup('')
    setSelectedRestaurantFilterId('')
    setPrefillRestaurantSubGroupName('')
    setPrefillRestaurantName('')
    setFiltersChanged(true)
  }

  const handleSelectRestaurant = (event) => {
    setSelectedRestaurantFilterId(event.target.value)
    setPrefillRestaurantName('')
    setFiltersChanged(true)
    setFiltersAutoApplyReady(false)
  }

  const handleRestaurantClear = () => {
    setSelectedRestaurantFilterId('')
    setPrefillRestaurantName('')
    setFiltersChanged(true)
    setFiltersAutoApplyReady(false)
  }

  const handleRestaurantGroupClear = () => {
    setSelectedRestaurantGroup('')
    setSelectedRestaurantSubGroup('')
    setSelectedRestaurantFilterId('')
    setRestaurantSubGroupSearch('')
    setPrefillRestaurantGroupName('')
    setPrefillRestaurantSubGroupName('')
    setPrefillRestaurantName('')
    setRestaurantGroupKey('')
    setCreatePermission(false)
    setDeleteInstancePermission(false)
    setAppVisibilityPermission(false)
    setFiltersChanged(true)
    setFiltersAutoApplyReady(false)
  }

  const handleReset = () => {
    setFiltersChanged(false);
    setFiltersAutoApplyReady(false);
    setSearch('');
    setRestaurantSearch('');
    setRestaurantGroupSearch('');
    setRestaurantSubGroupSearch('');
    setSelectedRestaurantGroup('');
    setSelectedRestaurantSubGroup('');
    setSelectedRestaurantFilterId('');
    setPrefillRestaurantGroupName('');
    setPrefillRestaurantSubGroupName('');
    setPrefillRestaurantName('');
    setCreatePermission(false);
    setDeleteInstancePermission(false);
    setAppVisibilityPermission(false);
    setOrderBy('created_at');
    setOrder('desc');
    setItemsPerPage(25);
    setPage(1);
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
  };

  const handleFilterChange = () => {
    const queryFilter = {
      restaurant_group_id: selectedRestaurantGroup,
      restaurant_sub_group_id: selectedRestaurantSubGroup,
      restaurant_id: selectedRestaurantFilterId,
    }
    setSearch('')
    setFilters(queryFilter);
    setAppliedFilters(queryFilter)
    setItemsPerPage(25);
    setPage(1);
    setOrderBy('created_at')
    setOrder('desc')
    setFiltersChanged(false);
    setFiltersAutoApplyReady(true)
  };

  const handleSortChange = (columnKey, sortDirection) => {
    setOrderBy(columnKey);
    setOrder(sortDirection);
    setPage(1);
  };

  const handleExportCsv = async () => {
    try {
      setCsvLoading(true);
      const newFormattedFilters = {
        restaurant_group_ids: appliedFilters?.restaurant_group_id ? [appliedFilters.restaurant_group_id] : undefined,
        restaurant_sub_group_id: appliedFilters?.restaurant_sub_group_id || undefined,
        restaurant_ids: appliedFilters?.restaurant_id ? [appliedFilters.restaurant_id] : undefined,
        search_query: debouncedSearchTerm,
      };
      const query = buildQueryParams(newFormattedFilters)
      const url = `${process.env.REACT_APP_API_BASE_URL}/v1/admin/csv_reports/export?report=restaurants&${query}`;

      const { response } = await authenticatedFetch(url, {
        method: 'GET',
        defaultErrorMessage: 'Failed to download CSV'
      });

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = downloadUrl;
      let date = new Date
      let filename = `restaurants_${dayjs(date).format('YYYY-MM-DD')}.csv`

      a.download = filename;
      a.click();

      window.URL.revokeObjectURL(downloadUrl);

      showNotification('CSV downloaded successfully', 'success');
    } catch (error) {
      console.error('CSV download error:', error);
      showNotification(error?.message || 'Failed to download CSV', 'error');
    } finally {
      setCsvLoading(false);
    }
  }

  const getPhoneNumberWithCountryCode = (data) => {
    let country_code = data?.phone_country_code || '+1'
    return country_code + data?.phone
  }

  const handleSearch = (searchQuery) => {
    if (hasActiveRestaurantFilters(appliedFilters) && restaurantData?.data) {
      setSearch(searchQuery)
      setPage(1)
   }
  }

  const TableRows = (data) => {
    return data?.map((record) => {
      const toastImportStatus = getToastImportStatus(record)
      const toastImportMeta = getToastImportMeta(record, theme)
      const isInvokeLoading = restaurantId === record?.id && toastImportStatus === TOAST_IMPORT_STATUS.INACTIVE
      const canCreateGuest =
        strictCanScope(ability, 'create_network_guest', 'Restaurant') ||
        strictCanScope(ability, 'create_network_guest', 'Restaurant', `${record?.id}`)

      return {
        id: record?.id,
        name: record?.name,
        email: record?.email,
        phone: getPhoneNumberWithCountryCode(record),
        cuisine: record?.cuisine,
        created_at: formatDate(record?.created_at, record?.time_zone_abbr	),
        departments: {
          display: <DepartmentsChip departments={record?.departments} testId={`restaurant-departments-${record?.id}`} />,
          sortValue: record?.departments?.length || 0,
        },
        sidekiq_jobs: {
          display: (
            <Box
              data-testid={`restaurant-job-sync-${record?.id}`}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.5,
              }}
            >
              <RestaurantLightTooltip title={toastImportMeta.tooltip} placement="top">
                <span style={{ cursor: toastImportMeta.cursor, display: 'inline-flex' }}>
                  <Chip
                    data-testid={`restaurant-job-sync-chip-${record?.id}`}
                    sx={{
                      backgroundColor: toastImportMeta.backgroundColor,
                      color: toastImportMeta.textColor,
                      width: '123px',
                      opacity: 1,
                      '&.Mui-disabled': {
                        backgroundColor: toastImportMeta.backgroundColor,
                        color: toastImportMeta.textColor,
                        opacity: 1,
                      },
                      "&:hover": {
                        backgroundColor: toastImportMeta.hoverBackgroundColor,
                        color: toastImportMeta.textColor,
                      },
                    }}
                    disabled={toastImportMeta.disabled || !strictCanScope(ability, 'fetch_and_imports', 'Restaurant', `${record?.id}`)}
                    label={isInvokeLoading ? <LoadingSpinner isLoading={true} /> : toastImportMeta.label}
                    onClick={() => handleToastImportClick(record, toastImportStatus, toastImportMeta)}
                  />
                </span>
              </RestaurantLightTooltip>
            </Box>
          ),
          sortValue: toastImportStatus === TOAST_IMPORT_STATUS.ACTIVE ? 'revoke' : 'invoke',
        },
        actions: (
          <React.Fragment>
            <div style={{ display: 'flex' }}>
              <Tooltip title='View' arrow>
                <IconButton onClick={() => navigate(`/admin/restaurant/update/${record?.id}`, {
                  state: {
                    restaurantId: record?.id,
                    restaurantName: record?.name,
                    restaurantGroupId: selectedRestaurantGroup,
                    restaurantGroupName: getRestaurantGroupName(selectedRestaurantGroup),
                    restaurantSubGroupId: selectedRestaurantSubGroup,
                    restaurantSubGroupName: getRestaurantSubGroupName(selectedRestaurantSubGroup)
                    }}
                  )}
                >
                  <VisibilityIcon
                    sx={{
                      color: '#707070',
                      cursor: 'pointer',
                    }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip
                title='Delete'
                arrow
              >
                <span>
                  <IconButton
                    onClick={() => {
                      setDialogOpen(true)
                      setSelectedRestaurant(record)
                    }}
                    disabled={!deleteInstancePermission}
                  >
                    <DeleteIcon
                      sx={{
                        color: !deleteInstancePermission ? "#e0e0e0" : "#707070",
                        cursor: "pointer",
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip
                title='Add mobile app users'
                arrow
              >
                <span>
                  <IconButton
                    onClick={(event) => handleOpenGuestCreateMenu(event, record)}
                    disabled={!canCreateGuest}
                    data-testid={`add-mobile-app-users-button-${record?.id}`}
                  >
                    <PersonAddAltOutlinedIcon
                      sx={{
                        color: !canCreateGuest ? "#e0e0e0" : "#707070",
                        cursor: "pointer",
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </React.Fragment>
        ),
        app_visibility: {
          display: (
            <React.Fragment>
              <Switch
                checked={record?.app_visibility}
                disabled={!(defaultCreatePermission || appVisibilityPermission)}
                onChange={() => {
                  setVisibilityDialogOpen(true)
                  setSelectedRestaurant(record)
                }}
                slotProps={{ input: { 'aria-label': 'controlled' } }}
              />
            </React.Fragment>
          ),
          sortValue: record?.app_visibility,
        },
        profile_completion: {
          display: (
            <StyledProgress
              success={{percent: record?.restaurant_progress}}
              percent={record?.restaurant_progress}
              strokeColor={'#52c41a'}
              type="circle"
              status="normal"
              strokeWidth={8}
              size={47}
            />
          ),
          sortValue: record?.restaurant_progress,
        },
        translate: {
          display: (
            <React.Fragment>
              {(strictCanScope(ability, "update", "Restaurant", `${record?.id}`) || strictCanScope(ability, "read", "Restaurant", `${record?.id}`)) && (
                <Tooltip title='Manage Translations' arrow>
                  <IconButton onClick={() => handleOpenTranslationModal(record)}>
                    <TranslateIcon
                      sx={{
                        color: '#707070',
                        cursor: 'pointer',
                      }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </React.Fragment>
          ),
          sortValue: false,
        }
      };
    });
  };

  const rows = TableRows(restaurantData?.data);

  return (
    <React.Fragment>
      <RestaurantPageContainer>
        <StyledBreadCrumbContainer>
          <Breadcrumbs>
            <NavigableBreadcrumbLink LinkComponent={StyledLink} label="Home" to="/admin/dashboard" />
            <StyledLink color={"#515152"}>Restaurants</StyledLink>
          </Breadcrumbs>
        </StyledBreadCrumbContainer>

        <FilterContainer data-testid='restaurant-filter-section'>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", gap: "18px" }}>
              <React.Fragment>
                <Typography
                  variant="body2"
                  sx={{
                    ...viewTypographyStyles,
                    alignSelf: { xs: "flex-start", sm: "center" },
                    whiteSpace: "nowrap",
                    mt: 0
                  }}
                >
                  View
                </Typography>
                <FormControl
                  size="small"
                  sx={{ width: { xs: "100%", sm: "160px" }, flexShrink: { xs: 1, sm: 0 } }}
                >
                  <StyledInputLabel>
                    Restaurant Group
                  </StyledInputLabel>
                  <Select
                    value={hasSingleAvailableRestaurantGroup ? restaurantGroupList?.[0]?.label : selectedRestaurantGroup}
                    renderValue={() =>
                      hasSingleAvailableRestaurantGroup ? restaurantGroupList?.[0]?.value : getRestaurantGroupName(selectedRestaurantGroup)
                    }
                    size="small"
                    label="Restaurant Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      width: "100%",
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleSelectRestaurantGroup}
                    onClose={() => setTimeout(() => setRestaurantGroupSearch(''), 1000)}
                    data-testid='restaurant-restaurant-group-select'
                    endAdornment={
                      selectedRestaurantGroup && !hasSingleAvailableRestaurantGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantGroupSearch} placeholder={'Search restaurant group'} isFetching={restaurantGroupFetching} />
                    {restaurantGroupList.map((res) => (
                      <StyledMenuItem
                        key={res?.label}
                        value={res?.label}
                      >
                        {
                          hasSingleAvailableRestaurantGroup || selectedRestaurantGroup === res?.label ? <SelectedMenuItem data={res?.value} /> : res?.value
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantGroupListPage}
                      targetRef={restaurantGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  disabled={!selectedRestaurantGroup}
                  sx={{ width: { xs: "100%", sm: "215px" }, flexShrink: { xs: 1, sm: 0 } }}
                >
                  <StyledInputLabel>
                    Restaurant Sub Group
                  </StyledInputLabel>
                  <Select
                    value={selectedRestaurantSubGroup || ''}
                    renderValue={() => getRestaurantSubGroupName(selectedRestaurantSubGroup)}
                    size="small"
                    label="Restaurant Sub Group"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantSubGroupMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      width: "100%",
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleSelectRestaurantSubGroup}
                    onClose={() => setTimeout(() => setRestaurantSubGroupSearch(''), 1000)}
                    data-testid='restaurant-restaurant-sub-group-select'
                    endAdornment={
                      selectedRestaurantSubGroup ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantSubGroupClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSubGroupSearch} placeholder={'Search sub group'} isFetching={restaurantSubGroupFetching} />
                    {restaurantSubGroupList.map((subGroup) => (
                      <StyledMenuItem
                        key={subGroup?.value}
                        value={subGroup?.value}
                      >
                        {
                          `${selectedRestaurantSubGroup}` === `${subGroup?.value}` ? <SelectedMenuItem data={subGroup?.label} /> : subGroup?.label
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantSubGroupListPage}
                      targetRef={restaurantSubGroupLoaderRef}
                    />
                  </Select>
                </FormControl>

                <FormControl
                  size="small"
                  sx={{ width: { xs: "100%", sm: "160px" }, flexShrink: { xs: 1, sm: 0 } }}
                >
                  <StyledInputLabel>
                    Restaurant
                  </StyledInputLabel>
                  <Select
                    value={hasSingleAvailableRestaurant ? restaurants?.[0]?.id : (selectedRestaurantFilterId || '')}
                    renderValue={() =>
                      hasSingleAvailableRestaurant ? restaurants?.[0]?.name : getRestaurantName(selectedRestaurantFilterId)
                    }
                    size="small"
                    label="Restaurant"
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      PaperProps: { ref: restaurantMenuRef, sx: { maxHeight: "40vh" } },
                      autoFocus: false,
                      MenuListProps: { sx: { pt: 0 } },
                    }}
                    sx={{
                      ...selectBaseStyles,
                      width: "100%",
                      borderColor: "#2b4fb9",
                    }}
                    onChange={handleSelectRestaurant}
                    onClose={() => setTimeout(() => setRestaurantSearch(''), 1000)}
                    data-testid='restaurant-select'
                    endAdornment={
                      // A single available restaurant is auto-applied and has nothing to fall back
                      // to, so the clear icon only appears once there is more than one to pick from.
                      selectedRestaurantFilterId && !hasSingleAvailableRestaurant ? (
                        <InputAdornment position="end" sx={{ position: 'absolute', right: 25 }}>
                          <IconButton
                            edge='end'
                            size="small"
                            disableRipple
                            onClick={handleRestaurantClear}
                          >
                            <ClearIcon sx={{ fontSize: '20px' }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }
                  >
                    <DropdownSearchBar changeSearchText={setRestaurantSearch} placeholder={'Search restaurant'} isFetching={restaurantFetching} />
                    {restaurants.map((res) => (
                      <StyledMenuItem
                        key={res?.id}
                        value={res?.id}
                      >
                        {
                          hasSingleAvailableRestaurant || selectedRestaurantFilterId === res?.id ? <SelectedMenuItem data={res?.name} /> : res?.name
                        }
                      </StyledMenuItem>
                    ))}
                    <DropdownInfiniteLoader
                      hasNextPage={hasNextRestaurantsPage}
                      targetRef={restaurantLoaderRef}
                    />
                  </Select>
                </FormControl>
              </React.Fragment>
            </Box>
            
            <Box sx={{ display: "flex", gap: "10px" }}>
              <ResetButton
                onClick={handleReset}
                status={isFetching}
              />

              <UpdateButton
                onClick={handleFilterChange}
                filtersChanged={filtersChanged}
                status={isFetching}
              />
            </Box>
          </Box>
        </FilterContainer>

        <StyledTitleContainer>
          <div style={{ display: "flex", gap: "30px", alignItems: "center" }}>
            <StyledTitle>Restaurants</StyledTitle>
          </div>
          <StyledSearchBar
            value={search}
            placeholder="Search"
            data-testid="restaurant-search-input"
            disabled={!hasActiveRestaurantFilters(appliedFilters) || !restaurantData?.data}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>),
            }}
            onChange={(e) => handleSearch(e.target.value)}
            autoComplete="off"
          />
        </StyledTitleContainer>

        <Divider style={{ borderStyle: "solid", borderWidth: "1px" }} />

        <div style={{ padding: "25px 0px", display: "flex", gap: "20px" }}>
          <StyledButton
            variant="contained"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            data-testid="restaurant-create-btn"
            disabled={!((defaultCreatePermission || createPermission) && Boolean(selectedRestaurantGroup))}
          >
            Create Restaurant
          </StyledButton>
        </div>

        <ConfirmationDialog
          open={dialogOpen}
          onClose={() => handleDialogClose()}
          Icon={CloseIcon}
          dialogTitle={'Are you sure you want to delete the restaurant?'}
          dialogBody={dialogBody()}
          confirmationText={'Yes, I want to delete the restaurant'}
          handleConfirm={handleDelete}
          isLoading={loadingStatus}
        />

        <ConfirmationDialog
          open={visibilityDialogOpen}
          onClose={() => setVisibilityDialogOpen(false)}
          Icon={CheckIcon}
          dialogTitle={'Are you sure you want to change the visibility of restaurant in mobile app?'}
          dialogBody={<React.Fragment>
            {'This action will change the visibility of'}
            <span style={{ fontWeight: "600" }}>
              {` '${selectedRestaurant?.name}'`}
            </span>
          </React.Fragment>}
          confirmationText={'Yes, I want to change the visibility of restaurant'}
          handleConfirm={handleVisibility}
          isLoading={loadingStatus}
        />

        <ConfirmationDialog
          open={revokeDialogOpen}
          onClose={() => setRevokeDialogOpen(false)}
          Icon={CheckIcon}
          dialogTitle={'Are you sure you want to revoke the sidekiq jobs scheduled for this restaurant?'}
          dialogBody={<React.Fragment>
            {'This action will revoke the sidekiq jobs of'}
            <span style={{ fontWeight: "600" }}>
              {` '${selectedRestaurant?.name}'`}
            </span>
          </React.Fragment>}
          confirmationText={'Yes, I want to revoke the sidekiq jobs'}
          handleConfirm={revokeBackgroundJobs}
          isLoading={loadingStatus}
        />

        <TranslationModal
          open={translationModalOpen}
          onClose={handleCloseTranslationModal}
          restaurantData={selectedRestaurantForTranslation}
          onSave={handleSaveTranslation}
          isLoading={isTranslationSaving}
          onLoadingChange={setTranslationLoading}
        />
  
        {/* Add mobile app users popover */}
        <AddMobileAppUsersMenu
          anchorEl={guestCreateAnchorEl}
          onClose={handleCloseGuestCreateMenu}
          onAddEmployee={handleOpenEmployeeCreateModal}
          onAddNetworkGuest={handleOpenNetworkGuestCreateModal}
          onBulkAddEmployees={handleOpenCsvImportModal}
        />

        {/* Single Employee Create Modal */}
        <EmployeeCreateModal
          open={employeeCreateModalOpen}
          onClose={() => setEmployeeCreateModalOpen(false)}
          restaurantId={activeGuestRestaurant?.id}
          restaurantName={activeGuestRestaurant?.name}
          restaurantGroupName={activeGuestRestaurant?.restaurant_group?.name || getRestaurantGroupName(selectedRestaurantGroup)}
        />

        {/* Single Network Guest Create Modal */}
        <NetworkGuestCreateModal
          open={networkGuestCreateModalOpen}
          onClose={() => setNetworkGuestCreateModalOpen(false)}
          restaurantId={activeGuestRestaurant?.id}
          restaurantName={activeGuestRestaurant?.name}
          restaurantGroupName={activeGuestRestaurant?.restaurant_group?.name || getRestaurantGroupName(selectedRestaurantGroup)}
        />

        {/* Bulk Employee CSV Import Modal */}
        <ManualCsvUploadEmployee
          open={csvImportModalOpen}
          onClose={handleCloseCsvImportModal}
          onImport={handleUploadEmployeesCsv}
          restaurants={activeGuestRestaurant?.id ? [{ value: activeGuestRestaurant.id, label: activeGuestRestaurant.name }] : []}
          disableRestaurantFilter
          defaultRestaurantId={activeGuestRestaurant?.id || ''}
          isUploading={importEmployeesCsvMutation.isPending}
        />

        {hasActiveRestaurantFilters(appliedFilters) && restaurantData?.data ? (
          <RestaurantTable
            data={rows}
            itemsPerPage={itemsPerPage}
            setItemsPerPage={setItemsPerPage}
            page={page}
            setPage={setPage}
            pageCount={restaurantData?.pageCount}
            fetchingLoader={isFetching}
            serverSort={true}
            onSortChange={handleSortChange}
            serverOrderBy={orderBy}
            serverOrder={order}
            showDownloadButton={true}
            disableDownloadButton={restaurantData?.data?.length === 0 || csvLoading}
            handleExportCsv={handleExportCsv}
            csvLoading={csvLoading}
            search={search}
            debouncedSearchTerm={debouncedSearchTerm}
          />) : isFetching && hasActiveRestaurantFilters(appliedFilters) ? (
            <Loader />
          ) : (
          <div>
            <Alert severity="info">Select a restaurant group or restaurant</Alert>
          </div>
        )
        }

      </RestaurantPageContainer>
    </React.Fragment>
  );
}
